/* eslint-disable lingui/no-unlocalized-strings -- U02: backend enum values; all user-facing labels are translated. */
import { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { useLingui } from '@lingui/react/macro';
import { getBillingSetting, billingSettingSourceForWallet, loadBillingSettingSheet } from '@/modules/billing-setting/public';
import { communicationSettingSource, loadCommunicationSettingSheet } from '@/modules/communication-setting/public';
import { EntityPicker } from '@/shared/form/entity-picker';
import { Input } from '@/shared/ui/input';
import { Select } from '@/shared/ui/select';
import { MoneyField } from '@/shared/ui/money-field';
import { PercentField } from '@/shared/ui/percent-field';
import { Button } from '@/shared/ui/button';
import { Plus } from 'lucide-react';
import type { BillingParameterValues } from './billing-workflow.model';
const ruleSchema = z.object({
  interestTypeValue: z.string().nullish(),
  fineTypeValue: z.string().nullish(),
  interestCodeValue: z.string().nullish(),
  interestAmount: z.union([z.number(), z.string()]).nullish(),
  fineAmount: z.union([z.number(), z.string()]).nullish(),
});
const emptyRule = {
  interestTypeValue: undefined,
  fineTypeValue: undefined,
  interestCodeValue: undefined,
  interestAmount: undefined,
  fineAmount: undefined,
};
const BillingSettingSheet = lazy(() =>
  loadBillingSettingSheet().then((module) => ({ default: module.BillingSettingSheet })),
);
const CommunicationSettingSheet = lazy(() =>
  loadCommunicationSettingSheet().then((module) => ({ default: module.CommunicationSettingSheet })),
);
const createdRuleSchema = z.looseObject({ id: z.string() });
export function BillingParameterFields({
  value,
  onChange,
  walletCode,
  onBusy,
  onError,
}: {
  value: BillingParameterValues;
  onChange: (v: BillingParameterValues) => void;
  walletCode: number;
  onBusy: (v: boolean) => void;
  onError: () => void;
}) {
  const { t } = useLingui();
  const [newRule, setNewRule] = useState<'billing' | 'communication' | null>(null);
  const latest = useRef({ value, onChange, onBusy, onError });
  useLayoutEffect(() => {
    latest.current = { value, onChange, onBusy, onError };
  });
  const id = value.billingSettingId;
  useEffect(() => {
    let active = true;
    if (!id) {
      latest.current.onChange({ ...latest.current.value, ...emptyRule });
      latest.current.onBusy(false);
      return;
    }
    latest.current.onBusy(true);
    void getBillingSetting(id)
      .then((raw) => {
        if (active && latest.current.value.billingSettingId === id)
          latest.current.onChange({ ...latest.current.value, ...ruleSchema.parse(raw) });
      })
      .catch(() => {
        if (active) latest.current.onError();
      })
      .finally(() => {
        if (active) latest.current.onBusy(false);
      });
    return () => {
      active = false;
      latest.current.onBusy(false);
    };
  }, [id, walletCode]);
  const fees = [
    { value: 'Free', label: t`Isento` },
    { value: 'Percent', label: t`Percentual` },
    { value: 'Money', label: t`Valor fixo` },
  ];
  return (
    <div className="grid gap-4">
      <label>
        {t`Régua de cobrança`}
        <EntityPicker
          source={billingSettingSourceForWallet(() => walletCode)}
          value={value.billingSettingId}
          onChange={(billingSettingId) => onChange({ ...value, ...emptyRule, billingSettingId })}
        />
      </label>
      <div className="flex justify-end">
        <Button type="button" variant="ghost" onClick={() => setNewRule('billing')}>
          <Plus aria-hidden />
          {t`Adicionar r\u00e9gua de cobran\u00e7a`}
        </Button>
      </div>
      <label>
        {t`Régua de comunicação`}
        <EntityPicker
          source={communicationSettingSource(() => walletCode)}
          value={value.communicationSettingId}
          onChange={(communicationSettingId) => onChange({ ...value, communicationSettingId })}
        />
      </label>
      <div className="flex justify-end">
        <Button type="button" variant="ghost" onClick={() => setNewRule('communication')}>
          <Plus aria-hidden />
          {t`Adicionar r\u00e9gua de comunica\u00e7\u00e3o`}
        </Button>
      </div>
      <label>
        {t`Data de vencimento`}
        <Input
          type="date"
          value={value.dueDate.slice(0, 10)}
          onChange={(e) => onChange({ ...value, dueDate: e.target.value })}
        />
      </label>
      <label className="flex gap-2">
        <input
          type="checkbox"
          checked={value.abatment}
          onChange={(e) =>
            onChange({
              ...value,
              abatment: e.target.checked,
              ...(e.target.checked ? { discountLimitDate: value.dueDate } : { discount: null }),
            })
          }
        />
        {t`Conceder desconto`}
      </label>
      {value.abatment && (
        <>
          <label>
            {t`Desconto`}
            <MoneyField
              required
              value={value.discount === null ? null : Number(value.discount)}
              onChange={(discount) =>
                onChange({ ...value, discount: discount === null ? null : String(discount) })
              }
            />
          </label>
          <label>
            {t`Data limite desconto`}
            <Input
              required
              type="date"
              value={value.discountLimitDate?.slice(0, 10) ?? ''}
              onChange={(e) => onChange({ ...value, discountLimitDate: e.target.value || null })}
            />
          </label>
        </>
      )}
      {(['interest', 'fine'] as const).map((kind) => {
        const type = kind === 'interest' ? value.interestTypeValue : value.fineTypeValue;
        const amount = kind === 'interest' ? value.interestAmount : value.fineAmount;
        const amountKey = kind === 'interest' ? 'interestAmount' : 'fineAmount';
        return (
          <fieldset key={kind} className="grid gap-2">
            <legend>{kind === 'interest' ? t`Juros` : t`Multa`}</legend>
            <Select
              value={type}
              options={fees}
              onChange={(typeValue) =>
                onChange({
                  ...value,
                  [kind === 'interest' ? 'interestTypeValue' : 'fineTypeValue']: typeValue,
                  ...(typeValue === 'Free'
                    ? {
                        [amountKey]: 0,
                        ...(kind === 'interest' ? { interestCodeValue: null } : {}),
                      }
                    : {}),
                })
              }
            />
            <label>
              {kind === 'interest' ? t`Taxa de juros` : t`Multa por atraso`}
              {type === 'Percent' ? (
                <PercentField
                  value={amount == null ? null : String(amount)}
                  onChange={(amountValue) => onChange({ ...value, [amountKey]: amountValue })}
                />
              ) : (
                <MoneyField
                  disabled={type === 'Free'}
                  value={amount == null ? null : Number(amount)}
                  onChange={(amountValue) => onChange({ ...value, [amountKey]: amountValue })}
                />
              )}
            </label>
          </fieldset>
        );
      })}
      {value.interestTypeValue !== 'Free' && (
        <label>
          {t`Código de juros`}
          <Select
            value={value.interestCodeValue}
            options={[
              { value: 'ValorDiasCorridos', label: t`Dias corridos` },
              { value: 'ValorDiasUteis', label: t`Dias úteis` },
            ]}
            onChange={(interestCodeValue) => onChange({ ...value, interestCodeValue })}
          />
        </label>
      )}
      <label>
        {t`Instrução de protesto`}
        <Select
          value={value.firstInstructionDebtCollection}
          options={[
            { value: '00', label: t`Não envia protesto` },
            { value: '06', label: t`Protestar automático` },
          ]}
          onChange={(firstInstructionDebtCollection) =>
            onChange({ ...value, firstInstructionDebtCollection })
          }
        />
      </label>
      {value.firstInstructionDebtCollection === '06' && (
        <label>
          {t`Dias após vencimento`}
          <Input
            required
            type="number"
            min={3}
            max={30}
            value={value.secondInstructionDebtCollection ?? ''}
            onChange={(e) =>
              onChange({
                ...value,
                secondInstructionDebtCollection:
                  e.target.value === '' ? null : Number(e.target.value),
              })
            }
          />
        </label>
      )}
      <Suspense fallback={null}>
        {newRule === 'billing' ? (
          <BillingSettingSheet
            open
            row={undefined}
            initialWalletCode={walletCode}
            onClose={() => setNewRule(null)}
          />
        ) : null}
        {newRule === 'communication' ? (
          <CommunicationSettingSheet
            editId="novo"
            initialWalletCode={walletCode}
            onClose={() => setNewRule(null)}
            onCreated={(result) => {
              const created = createdRuleSchema.safeParse(result);
              if (created.success) onChange({ ...value, communicationSettingId: created.data.id });
            }}
          />
        ) : null}
      </Suspense>
    </div>
  );
}
